import { useEffect, useRef, useState, useCallback } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Droplet, Trash2, X, Waves } from "lucide-react";
import { MapShell } from "@/components/MapShell";
import { makeBaseLayer } from "@/lib/mapBase";

const WATER_TYPES = [
  { value: "Brandkraan", label: "Brandkraan", color: "#0ea5e9" },
  { value: "Blusvijver", label: "Blusvijver", color: "#0d9488" },
];
const typeColor = (t) => (WATER_TYPES.find((x) => x.value === t) || WATER_TYPES[0]).color;
const icon = (t) => L.divIcon({ className: "", html: `<div style="width:16px;height:16px;border-radius:50% 50% 50% 0;background:${typeColor(t)};border:2px solid #fff;transform:rotate(-45deg);box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`, iconAnchor: [8, 14] });

export function WaterMapEditor({ canWrite }) {
  const el = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const baseRef = useRef(null);
  const [points, setPoints] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [area, setArea] = useState(null);
  const [ready, setReady] = useState(false);
  const [mapCfg, setMapCfg] = useState(null);
  const [sat, setSat] = useState(false);
  const [pending, setPending] = useState(null); // { lat, lng } — new point being added
  const [pname, setPname] = useState("");
  const [pnote, setPnote] = useState("");
  const [ptype, setPtype] = useState("Brandkraan");
  const [pcap, setPcap] = useState("");
  const [pcov, setPcov] = useState("");
  const [cov, setCov] = useState({ Brandkraan: 200, Blusvijver: 300 });
  const [savingCov, setSavingCov] = useState(false);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = useCallback(async () => {
    const [w, b, s] = await Promise.all([api.get("/water-points"), api.get("/buildings"), api.get("/settings/service-area").catch(() => ({ data: {} }))]);
    setPoints(w.data); setBuildings(b.data); setArea(s.data.polygon || null); setReady(true);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/settings/water-coverage").then(({ data }) => setCov(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/settings/map").then(({ data }) => setMapCfg(data)).catch(() => {}); }, []);

  const saveCov = async () => {
    setSavingCov(true);
    try { await api.put("/settings/water-coverage", cov); toast.success("Dekkingsstraal opgeslagen"); }
    catch (e) { toast.error("Opslaan mislukt"); }
    finally { setSavingCov(false); }
  };

  useEffect(() => {
    if (!ready || !el.current || mapRef.current) return;
    const c = buildings[0] ? [buildings[0].lat, buildings[0].lng] : [52.11, 5.18];
    const map = L.map(el.current, { attributionControl: false }).setView(c, 15);
    mapRef.current = map;
    baseRef.current = makeBaseLayer(mapCfg, false).addTo(map);
    buildings.forEach((b) => L.marker([b.lat, b.lng]).addTo(map).bindPopup(b.name));
    if (area && area.length >= 3) { try { map.fitBounds(L.polygon(area).getBounds().pad(0.12)); } catch (e) {} }
    if (canWrite) {
      let pt = null; let dll = null;
      const openAdd = (latlng) => { setPname(""); setPnote(""); setPtype("Brandkraan"); setPcap(""); setPcov(""); setPending({ lat: Number(latlng.lat.toFixed(6)), lng: Number(latlng.lng.toFixed(6)) }); };
      map.on("mousedown", (e) => { dll = e.latlng; clearTimeout(pt); pt = setTimeout(() => openAdd(dll), 500); });
      map.on("mouseup", () => clearTimeout(pt));
      map.on("movestart", () => clearTimeout(pt));
      map.on("contextmenu", (e) => { try { L.DomEvent.preventDefault(e.originalEvent); } catch (_) {} openAdd(e.latlng); });
    }
    setTimeout(() => map.invalidateSize(), 200);
  }, [ready, buildings, canWrite, area]);

  // Swap base layer when config loads or satellite toggles
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    if (baseRef.current) map.removeLayer(baseRef.current);
    baseRef.current = makeBaseLayer(mapCfg, sat).addTo(map);
    baseRef.current.bringToBack();
    if (!sat && mapCfg && mapCfg.mode === "static" && mapCfg.bounds) { try { map.fitBounds(mapCfg.bounds); } catch (e) {} }
  }, [mapCfg, sat]);

  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    // rebuild markers so type/colour changes are reflected
    Object.values(markersRef.current).forEach((m) => map.removeLayer(m));
    markersRef.current = {};
    points.forEach((w) => {
      const m = L.marker([w.lat, w.lng], { icon: icon(w.type), draggable: canWrite }).addTo(map).bindPopup(`${w.type === "Blusvijver" ? "🌊" : "💧"} ${w.name}${w.capacity ? " · " + w.capacity + " l/min" : ""}`);
      if (canWrite) m.on("dragend", async (e) => { const { lat, lng } = e.target.getLatLng(); try { await api.put(`/water-points/${w.id}`, { name: w.name, note: w.note || "", type: w.type || "Brandkraan", capacity: w.capacity ?? null, coverage_m: w.coverage_m ?? null, lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) }); load(); } catch (er) {} });
      markersRef.current[w.id] = m;
    });
  }, [points, canWrite, load]);

  const savePending = async () => {
    if (!pname.trim()) { toast.error("Geef een naam op"); return; }
    setSaving(true);
    try {
      await api.post("/water-points", { name: pname.trim(), lat: pending.lat, lng: pending.lng, note: pnote.trim(), type: ptype, capacity: pcap ? Number(pcap) : null, coverage_m: pcov ? Number(pcov) : null });
      toast.success(`${ptype} toegevoegd`);
      setPending(null); setPname(""); setPnote(""); setPcap(""); setPcov(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/water-points/${delTarget.id}`); toast.success("Bluswaterpunt verwijderd"); setDelTarget(null); load(); } catch (e) { setDelTarget(null); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="water-editor">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900"><Droplet style={{ width: 16, height: 16 }} className="text-sky-600" /> Bluswaterpunten {canWrite && <span className="text-xs font-normal text-slate-400">· klik-en-houd (rechtsklik / long-press) op de kaart om toe te voegen, sleep om te verplaatsen</span>}</div>
      {canWrite && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 p-2.5 text-xs" data-testid="coverage-settings">
          <span className="font-semibold text-slate-600">Standaard dekkingsstraal (m):</span>
          <label className="flex items-center gap-1 text-slate-600">Brandkraan <input data-testid="cov-brandkraan" type="number" min="0" value={cov.Brandkraan} onChange={(e) => setCov({ ...cov, Brandkraan: Number(e.target.value) })} className="w-20 rounded border border-slate-200 px-2 py-1" /></label>
          <label className="flex items-center gap-1 text-slate-600">Blusvijver <input data-testid="cov-blusvijver" type="number" min="0" value={cov.Blusvijver} onChange={(e) => setCov({ ...cov, Blusvijver: Number(e.target.value) })} className="w-20 rounded border border-slate-200 px-2 py-1" /></label>
          <button data-testid="cov-save" onClick={saveCov} disabled={savingCov} className="rounded-lg bg-slate-900 px-3 py-1 font-semibold text-white disabled:opacity-60">Opslaan</button>
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-4">
        <div className="max-h-[420px] space-y-2 overflow-y-auto lg:col-span-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Punten ({points.length})</div>
          {points.map((w) => (
            <div key={w.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-sky-50 p-2.5">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: typeColor(w.type) }} />
                  <div className="truncate text-sm font-semibold text-slate-900">{w.name}</div>
                </div>
                <div className="truncate text-[11px] text-slate-500">{w.type || "Brandkraan"}{w.capacity ? ` · ${w.capacity} l/min` : ""} · {Number(w.lat).toFixed(4)}, {Number(w.lng).toFixed(4)}</div>
              </div>
              {canWrite && <button data-testid={`water-delete-${w.id}`} onClick={() => setDelTarget(w)} className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
        <MapShell heightClass="h-[420px] lg:col-span-3" testid="water-map" onToggle={() => setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 80)}>
          <button data-testid="water-satellite-toggle" onClick={() => setSat((s) => !s)}
            className={`absolute left-3 top-3 z-[1001] rounded-lg border px-3 py-1.5 text-xs font-semibold shadow ${sat ? "border-blue-500 bg-blue-600 text-white" : "border-slate-200 bg-white/95 text-slate-700 hover:bg-white"}`}>
            {sat ? "Satelliet aan" : "Satelliet"}
          </button>
          <div ref={el} data-testid="water-map" className="h-full w-full overflow-hidden rounded-xl border border-slate-200" />
        </MapShell>
      </div>

      {pending && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/50 p-4" onClick={() => setPending(null)}>
          <div data-testid="water-add-dialog" className="w-full max-w-sm rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <h3 className="flex items-center gap-2 font-display font-bold text-slate-900"><Droplet className="h-4 w-4 text-sky-600" /> Bluswaterpunt toevoegen</h3>
              <button onClick={() => setPending(null)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3 p-6">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {WATER_TYPES.map((t) => (
                    <button key={t.value} type="button" data-testid={`water-type-${t.value}`} onClick={() => setPtype(t.value)}
                      className={`flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${ptype === t.value ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                      {t.value === "Blusvijver" ? <Waves style={{ width: 15, height: 15 }} /> : <Droplet style={{ width: 15, height: 15 }} />} {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Naam</label>
                <input data-testid="water-name-input" autoFocus value={pname} onChange={(e) => setPname(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") savePending(); }}
                  placeholder={ptype === "Blusvijver" ? "Bijv. Blusvijver Noord" : "Bijv. Brandkraan 1"} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-sky-500" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Notitie (optioneel)</label>
                <input data-testid="water-note-input" value={pnote} onChange={(e) => setPnote(e.target.value)}
                  placeholder="Bijv. type aansluiting, bijzonderheden" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-sky-500" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Capaciteit (l/min)</label>
                <input data-testid="water-capacity-input" type="number" min="0" value={pcap} onChange={(e) => setPcap(e.target.value)}
                  placeholder="Bijv. 1000" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-sky-500" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Dekkingsstraal (m, optioneel)</label>
                <input data-testid="water-coverage-input" type="number" min="0" value={pcov} onChange={(e) => setPcov(e.target.value)}
                  placeholder={`Standaard ${cov[ptype] || 200} m`} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-sky-500" />
              </div>
              <div className="text-[11px] text-slate-400">Locatie: {pending.lat}, {pending.lng}</div>
              <div className="flex gap-2 pt-1">
                <button data-testid="water-save-btn" onClick={savePending} disabled={saving} className="flex-1 rounded-xl bg-sky-600 py-2.5 text-sm font-semibold text-white hover:bg-sky-500 disabled:opacity-60">Opslaan</button>
                <button onClick={() => setPending(null)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Annuleren</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {delTarget && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/50 p-4" onClick={() => setDelTarget(null)}>
          <div data-testid="water-delete-dialog" className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-slate-900">Bluswaterpunt verwijderen?</h3>
            <p className="mt-1 text-sm text-slate-500">"{delTarget.name}" wordt permanent verwijderd.</p>
            <div className="mt-4 flex gap-2">
              <button data-testid="water-confirm-delete" onClick={confirmDelete} className="flex-1 rounded-xl bg-red-600 py-2.5 text-sm font-semibold text-white hover:bg-red-500">Verwijderen</button>
              <button onClick={() => setDelTarget(null)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Annuleren</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
